import React, { useState, useEffect, useCallback } from 'react'

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
}

/**
 * Notification Context
 */
const NotificationContext = React.createContext()

/**
 * Notification Provider
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([])

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random()
    const newNotification = {
      id,
      type: NOTIFICATION_TYPES.INFO,
      title: '',
      message: '',
      duration: 5000,
      persistent: false,
      actions: [],
      ...notification
    }

    setNotifications(prev => [...prev, newNotification])

    // Auto-remove notification after duration
    if (!newNotification.persistent && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }, [])

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }, [])

  const clearAllNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.SUCCESS,
      title: 'Success',
      message,
      ...options
    })
  }, [addNotification])

  const showError = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.ERROR,
      title: 'Error',
      message,
      persistent: true,
      ...options
    })
  }, [addNotification])

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.WARNING,
      title: 'Warning',
      message,
      ...options
    })
  }, [addNotification])

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.INFO,
      title: 'Info',
      message,
      ...options
    })
  }, [addNotification])

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

/**
 * Hook to use notifications
 */
export const useNotifications = () => {
  const context = React.useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

/**
 * Individual Notification Component
 */
const Notification = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleRemove = () => {
    setIsVisible(false)
    setTimeout(() => onRemove(notification.id), 300)
  }

  const getIcon = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return 'fas fa-check-circle'
      case NOTIFICATION_TYPES.ERROR:
        return 'fas fa-exclamation-circle'
      case NOTIFICATION_TYPES.WARNING:
        return 'fas fa-exclamation-triangle'
      case NOTIFICATION_TYPES.INFO:
        return 'fas fa-info-circle'
      default:
        return 'fas fa-info-circle'
    }
  }

  const getColorClasses = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return 'bg-gradient-to-br from-green-900/90 via-green-800/80 to-green-900/90 border-green-500/50 text-green-100 backdrop-blur-sm shadow-lg shadow-green-500/20'
      case NOTIFICATION_TYPES.ERROR:
        return 'bg-gradient-to-br from-red-900/90 via-red-800/80 to-red-900/90 border-red-500/50 text-white backdrop-blur-sm shadow-lg shadow-red-500/20'
      case NOTIFICATION_TYPES.WARNING:
        return 'bg-gradient-to-br from-yellow-900/90 via-yellow-800/80 to-yellow-900/90 border-yellow-500/50 text-white backdrop-blur-sm shadow-lg shadow-yellow-500/20'
      case NOTIFICATION_TYPES.INFO:
        return 'bg-gradient-to-br from-blue-900/90 via-blue-800/80 to-blue-900/90 border-blue-500/50 text-blue-100 backdrop-blur-sm shadow-lg shadow-blue-500/20'
      default:
        return 'bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 border-gray-500/50 text-gray-100 backdrop-blur-sm shadow-lg shadow-gray-500/20'
    }
  }

  return (
    <div
      className={`rounded-xl border p-4 mb-3 transition-all duration-300 transform ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      } ${getColorClasses()}`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <i className={`${getIcon()} text-lg`}></i>
        </div>
        <div className="ml-3 flex-1">
          {notification.title && (
            <h4 className="text-sm font-semibold mb-1">{notification.title}</h4>
          )}
          <p className="text-sm leading-relaxed">{notification.message}</p>
          {notification.actions && notification.actions.length > 0 && (
            <div className="mt-3 flex space-x-2">
              {notification.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="text-xs font-medium underline hover:no-underline transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={handleRemove}
            className="text-secondary hover:text-white transition-colors"
            aria-label="Close notification"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Notification Container Component
 */
export const NotificationContainer = ({ position = 'top-right' }) => {
  const { notifications, removeNotification } = useNotifications()

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  }

  if (notifications.length === 0) return null

  return (
    <div className={`fixed z-50 max-w-sm w-full ${positionClasses[position]}`}>
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  )
}

/**
 * Progress Notification Component
 */
export const ProgressNotification = ({ 
  isVisible = false,
  title = 'Processing...',
  progress = { current: 0, total: 100 },
  onCancel = null,
  className = ''
}) => {
  if (!isVisible) return null

  const percentage = Math.min(100, Math.max(0, (progress.current / progress.total) * 100))

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${className}`}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-primary mb-2">{title}</h3>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <p className="text-sm text-secondary mb-4">
            {progress.current} of {progress.total} steps completed ({Math.round(percentage)}%)
          </p>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-secondary hover:text-primary border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}